/*
 * Lemonade Training Oy copy rights all right reserved.
 * F032 step 3: the Members page Overview and each member's recent classes.
 */

/* ------------------------------ Thresholds ------------------------------ */

/** Days without a class before someone is "Not seen". Owner decision 2026-09-26. */
export const MEMBER_NOT_SEEN_DAYS = 14;
/** A member is "New" for this many days after joining. */
export const MEMBER_NEW_DAYS = 30;
/**
 * A gym's existing members move over in its first days on Lemonade. Anyone who
 * joined within this many days of the gym's start is never "New".
 */
export const MEMBER_NEW_GYM_START_GRACE_DAYS = 14;
/** "Slowing down": the last N weeks compared with the M weeks before them. */
export const MEMBER_SLOWING_RECENT_WEEKS = 3;
export const MEMBER_SLOWING_BASELINE_WEEKS = 8;
/** Recent weekly rate below this share of the usual rate counts as slowing down. */
export const MEMBER_SLOWING_RATIO = 0.5;
/**
 * The usual rhythm uses up to MEMBER_SLOWING_BASELINE_WEEKS of history before
 * the recent weeks, and needs at least this many to judge at all.
 */
export const MEMBER_SLOWING_MIN_BASELINE_WEEKS = 3;
/** Only people who usually come at least this often a week can be "slowing down". */
export const MEMBER_SLOWING_MIN_USUAL_PER_WEEK = 1;
/** A new member with no class this many days after joining is flagged. */
export const MEMBER_NO_FIRST_CLASS_AFTER_DAYS = 2;
/** First-month path: joined → 1st → 3rd → this many classes. */
export const MEMBER_FIRST_MONTH_TARGET_CLASSES = 8;
/** Visit cards with this many visits or fewer left are flagged to owners. */
export const MEMBER_VISITS_LOW = 1;
/** Class counts worth celebrating (prior classes the gym recorded included). */
export const MEMBER_CLASS_MILESTONES = [
  10, 25, 50, 100, 150, 200, 250, 300, 400, 500, 750, 1000,
] as const;
/** Weeks of per-class history kept on each member's activity document. */
export const MEMBER_ACTIVITY_WINDOW_WEEKS = 12;
/** Longest list the overview stores per section. */
export const MEMBER_OVERVIEW_LIST_LIMIT = 25;

/* --------------------------- Member activity ---------------------------- */

/** Subcollection under Boxes/{boxID}; server-written, never read by clients. */
export const MEMBER_ACTIVITY_COLLECTION = "MemberActivity";

/**
 * One member's classes at one gym, by CLASS time. A class counts once it has
 * passed with the booking checked in or marked no-show (gyms that never check
 * people in get every past booking marked no-show). `recent` maps classID to
 * the class start in epoch milliseconds, so recording a class twice is
 * harmless; entries older than the window move into `olderCount`.
 */
export type MemberActivityDoc = {
  uid: string;
  /** The name on the member's latest booking; "" when none. */
  name: string;
  recent: Record<string, number>;
  olderCount: number;
  /** Epoch ms of the first and latest class ever recorded; null before any. */
  firstClassAt: number | null;
  lastClassAt: number | null;
  updatedAt: unknown;
};

/* ------------------------------- Overview ------------------------------- */

export const MEMBER_STAGES = [
  "new",
  "regular",
  "slowing",
  "not_seen",
  "paused_or_leaving",
] as const;
export type MemberStage = (typeof MEMBER_STAGES)[number];

/** Most urgent first; the order the list is sorted in. */
export const MEMBER_REACH_OUT_REASONS = [
  "payment_failed",
  "no_first_class",
  "not_seen",
  "slowing_down",
  "visit_card_low",
] as const;
export type MemberReachOutReason = (typeof MEMBER_REACH_OUT_REASONS)[number];

/** Reasons only a gym's owners and admins ever receive. */
export const MEMBER_OWNER_ONLY_REASONS: readonly MemberReachOutReason[] = [
  "payment_failed",
  "visit_card_low",
];

export type MemberReachOutItem = {
  uid: string;
  name: string;
  reason: MemberReachOutReason;
  /** Days since their last class; null when they never had one. */
  daysSinceLastClass: number | null;
  /** Classes a week over the usual (baseline) weeks; null when not measured. */
  usualPerWeek: number | null;
  /** Classes in the recent weeks; null when not measured. */
  recentClasses: number | null;
  /** Days since joining; null when unknown. */
  joinedDaysAgo: number | null;
  /** payment_failed: ISO end of the grace period, or null when already paused. */
  graceEndsAt: string | null;
  /** visit_card_low: visits left. */
  visitsRemaining: number | null;
};

export const MEMBER_CELEBRATION_KINDS = [
  "class_milestone",
  "first_full_week",
] as const;
export type MemberCelebrationKind = (typeof MEMBER_CELEBRATION_KINDS)[number];

export type MemberCelebration = {
  uid: string;
  name: string;
  kind: MemberCelebrationKind;
  /** class_milestone: the class number (e.g. 100). */
  milestone: number | null;
  /** "upcoming": booked in the next 7 days; "reached": in the last 7 days. */
  when: "upcoming" | "reached";
  /** ISO start of the class that reaches or reached it; null for first_full_week. */
  classStart: string | null;
  /** first_full_week: classes in their first 7 days. */
  classesInFirstWeek: number | null;
};

export const MEMBER_FIRST_MONTH_STATES = [
  "no_class_yet",
  "slow_start",
  "on_track",
] as const;
export type MemberFirstMonthState = (typeof MEMBER_FIRST_MONTH_STATES)[number];

export type MemberFirstMonthItem = {
  uid: string;
  name: string;
  /** ISO join date. */
  joinedAt: string;
  /** Classes since joining. */
  classes: number;
  state: MemberFirstMonthState;
};

/** Written by the server under Boxes/{boxID}/Stats; served through getMemberOverview. */
export const MEMBER_OVERVIEW_DOC_ID = "memberOverview";

export type MemberOverview = {
  /** ISO time the overview was computed; null before the first run. */
  generatedAt: string | null;
  stageCounts: Record<MemberStage, number>;
  /** Of the stage "paused_or_leaving": how many come back within 14 days. */
  returningSoon: number;
  /** Most urgent first, at most MEMBER_OVERVIEW_LIST_LIMIT. */
  reachOut: MemberReachOutItem[];
  reachOutTotal: number;
  celebrations: MemberCelebration[];
  /** Needing help first (no class yet, slow start), then on track. */
  firstMonth: MemberFirstMonthItem[];
  firstMonthTotal: number;
};

/** The stored Stats/memberOverview document: the owner view plus the coach view. */
/**
 * "paused_or_leaving" comes from memberships, which coaches never see: the
 * coach view counts those people in the stage their classes put them in, and
 * its paused_or_leaving count is always 0.
 */
export type MemberOverviewStoredDoc = MemberOverview & {
  coachStageCounts: Record<MemberStage, number>;
  /** Each person with the most urgent reason a coach may see (no owner-only reasons). */
  coachReachOut: MemberReachOutItem[];
  coachReachOutTotal: number;
  /** The zone the gym days were counted in. */
  timeZone: string;
  updatedAt: unknown;
};

/** Stats/activityCursor: how far the activity recorder has read a gym's classes. */
export const MEMBER_ACTIVITY_CURSOR_DOC_ID = "activityCursor";
export type MemberActivityCursorDoc = {
  /** Epoch ms of the last recorded class's end. */
  processedThroughMs: number;
  /** The same instant as a Timestamp, with the class id, so equal end times are not skipped. */
  lastEndTime: unknown;
  lastClassID: string;
  updatedAt: unknown;
};

/**
 * MemberOverviewRefreshState/cursor: where the 3-hourly job stopped when it ran
 * out of time, so the next run continues there instead of starving the last gyms.
 */
export const MEMBER_OVERVIEW_REFRESH_STATE_COLLECTION =
  "MemberOverviewRefreshState";
export type MemberOverviewRefreshStateDoc = {
  /** The last gym started, or null after a full lap. */
  lastBoxID: string | null;
  updatedAt: unknown;
  lastRun: { boxes: number; done: number; skipped: number; remaining: number };
};

export type GetMemberOverviewRequest = { boxID: string };

export type GetMemberOverviewResponse = MemberOverview & {
  /** "all" for owners and admins; "coach" strips owner-only reasons and their counts. */
  scope: "all" | "coach";
};

/* ------------------------ Members list (roster) ------------------------- */

/** Owner-facing status in the Members list. */
export const MEMBER_LIST_STATUSES = [
  "active",
  "new",
  "paused",
  "payment_failed",
  "leaving",
] as const;
export type MemberListStatus = (typeof MEMBER_LIST_STATUSES)[number];

export const isMemberStage = (v: unknown): v is MemberStage =>
  typeof v === "string" && (MEMBER_STAGES as readonly string[]).includes(v);
export const isMemberReachOutReason = (v: unknown): v is MemberReachOutReason =>
  typeof v === "string" &&
  (MEMBER_REACH_OUT_REASONS as readonly string[]).includes(v);
export const isMemberListStatus = (v: unknown): v is MemberListStatus =>
  typeof v === "string" &&
  (MEMBER_LIST_STATUSES as readonly string[]).includes(v);

/** The next milestone above `count`, or null past the last one. */
export const nextClassMilestone = (count: number): number | null =>
  MEMBER_CLASS_MILESTONES.find((m) => m > count) ?? null;
